import { Loading } from "@components/Loading";
import { QuantizedSlider } from "@components/QuantizedSlider";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { Timestamp } from "firebase/firestore";
import { useContext, useMemo, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useParams } from "react-router-dom";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useSimpleDocument } from "src/helpers/useSimpleDocument";
import { useToast } from "src/helpers/useToast";
import { OracleNav } from "./OracleNav";

const EXCHANGE_SENTINEL = "_exchange";
const PHOTO_PRIZE_COST = 50; // a single photo prize costs a fixed 50 points

type TransactionRow = PointTransaction & { id: string };

const formatTxDate = (ts?: Timestamp): string => {
  const date = ts?.toDate?.();
  if (!date) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/**
 * The associate's per-team screen. Two journeys, one screen each (no tabs):
 * - Arbitration (entered via a challenge): ADD points (a deposit, capped per-award at the challenge MAX).
 * - Point Exchange (entered via the dashboard button): redeem a fixed-cost photo prize (one click = −50).
 * Both share one append-only points ledger (`pointTransactions`), shown as history at the bottom.
 */
export const OracleTeam = () => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const { challengeId, teamId } = useParams();
  const { functions } = useContext(FirebaseContext);
  const toast = useToast();
  const { getTranslatedValue } = useTranslatedKey();

  const isExchangeOnly = challengeId === EXCHANGE_SENTINEL;

  const [team, teamLoading] = useSimpleDocument<Team>(`events/${eventId}/teams/${teamId}`);
  const [challenge] = useSimpleDocument<Challenge>(
    isExchangeOnly
      ? `events/${eventId}/challenges/${EXCHANGE_SENTINEL}`
      : `events/${eventId}/challenges/${challengeId}`,
  );

  const isOracleChallenge = !isExchangeOnly && challenge?.type === "oracle";
  const maxAward = isOracleChallenge ? challenge.reward ?? 0 : 0;
  const currentPoints = team?.points ?? 0;

  // Each exchange redeems exactly one fixed-cost photo prize. The team needs at least the cost to redeem;
  // multiples are just repeated clicks.
  const canExchange = currentPoints >= PHOTO_PRIZE_COST;

  // One shared ledger for awards (+) and exchanges (−), newest first.
  const [transactions] = useSimpleCollection<TransactionRow>(`events/${eventId}/teams/${teamId}/pointTransactions`);
  const ledger = useMemo(
    () => [...transactions].sort((a, b) => (b.createdOn?.toMillis?.() ?? 0) - (a.createdOn?.toMillis?.() ?? 0)),
    [transactions],
  );

  // Arbitration: ADD points. The slider is the amount to add this time (0..MAX); resets to 0 after.
  const [awardValue, setAwardValue] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [awardPoints] = useHttpsCallable<API.OracleAwardChallengePointsRequest, API.OracleAwardChallengePointsResponse>(
    functions,
    "oracleAwardChallengePointsV2",
  );
  const [exchangePoints] = useHttpsCallable<API.OracleExchangePointsRequest, API.OracleExchangePointsResponse>(
    functions,
    "oracleExchangePointsV2",
  );

  const onAward = async () => {
    if (!isOracleChallenge || awardValue <= 0) return;
    try {
      setSubmitting(true);
      await awardPoints({ eventId, teamId: teamId!, challengeId: challengeId!, points: awardValue });
      toast(t("pointsAwarded"), "🎉");
      setAwardValue(0); // additive — reset so the associate can add again
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const onExchange = async () => {
    if (!canExchange) return;
    try {
      setSubmitting(true);
      await exchangePoints({ eventId, teamId: teamId!, amount: PHOTO_PRIZE_COST, note: "" });
      toast(t("pointsExchanged"), "👍");
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <OracleNav backLink={isExchangeOnly ? "/oracle" : `/oracle/${challengeId}/teams`} />
      <main className="flex flex-1 flex-col items-center px-6 pb-24">
        <Loading loading={teamLoading}>
          <div className="mt-10 mb-6 text-center">
            <div className="text-2xl font-semibold">{team?.displayName ?? team?.name}</div>
            <div className="mt-1 text-sm text-gray-500">{t("teamTotalPoints")}</div>
            <div className="text-4xl font-bold tabular-nums">{currentPoints}</div>
          </div>

          {/* One screen per journey: arbitration (entered via a challenge) OR point exchange (entered
              via the dashboard's Point Exchange button) — no tab switching. */}
          {isOracleChallenge && (
            <div className="flex w-full max-w-sm flex-col items-center">
              <div className="mb-1 text-center text-sm font-semibold uppercase text-gray-500">{t("arbitration")}</div>
              <div className="mb-4 text-center text-lg font-medium">{getTranslatedValue(challenge, "title")}</div>
              <QuantizedSlider max={maxAward} value={awardValue} onChange={setAwardValue} />
              <button
                onClick={onAward}
                disabled={submitting || awardValue <= 0}
                className={`btn-primary btn mt-8 w-full ${submitting ? "loading" : ""}`}
              >
                {t("addPoints")}
              </button>
            </div>
          )}

          {isExchangeOnly && (
            <div className="flex w-full max-w-sm flex-col items-center">
              <div className="mb-1 text-center text-sm font-semibold uppercase text-gray-500">{t("pointExchange")}</div>
              <div className="mb-6 text-center text-sm text-gray-500">
                {t("photoPrizeHint", { cost: PHOTO_PRIZE_COST })}
              </div>

              {/* One click redeems one fixed-cost photo prize. Multiples = multiple clicks. */}
              <button
                onClick={onExchange}
                disabled={submitting || !canExchange}
                className={`btn-error btn w-full text-white ${submitting ? "loading" : ""}`}
              >
                {t("exchangePhotoPrize")} (−{PHOTO_PRIZE_COST})
              </button>

              {!canExchange && (
                <div className="mt-3 text-center text-sm text-gray-400">
                  {t("exchangeMinNotMet", { min: PHOTO_PRIZE_COST })}
                </div>
              )}
            </div>
          )}

          {/* Transparent, append-only history of every award (+) and exchange (−), with who did it. */}
          <div className="mt-10 w-full max-w-sm">
            <h3 className="mb-2 text-sm font-semibold uppercase text-gray-500">{t("pointsHistory")}</h3>
            {ledger.length === 0 ? (
              <div className="text-sm text-gray-400">{t("noTransactionsYet")}</div>
            ) : (
              <div className="flex flex-col">
                {ledger.map((tx) => {
                  const isAward = tx.amount >= 0;
                  return (
                    <div key={tx.id} className="flex items-start justify-between border-b border-base-200 py-2">
                      <div className="flex flex-col">
                        <span className={`font-semibold tabular-nums ${isAward ? "text-success" : "text-error"}`}>
                          {isAward ? "+" : "−"}
                          {Math.abs(tx.amount)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {tx.byName}
                          {tx.note ? ` · ${tx.note}` : ""}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{formatTxDate(tx.createdOn)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Loading>
      </main>
    </div>
  );
};
